import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../../utils/useAxios";
import { resObj, paginationVal } from "../../../utils/constants";
import PermissionContext from "../../../context/PermissionContext";
import DrawerContext from "../../../context/DrawerContext";
import AutoLogout from "../../../utils/useAutoLogout";

import Pagination from "@mui/material/Pagination";

import SideDrawer from "../../../components/layout/SideDrawer";
import ListHeader from "../../../components/layout/ListHeader";
import CustomTable from "../../../components/layout/CustomTable";
import NoData from "../../../components/layout/NoData";
import SuccessAlert from "../../../components/layout/SuccessAlert";
import ErrorModal from "../../../components/layout/ErrorModal";
import DeleteModal from "../../../components/layout/DeleteModal";
import Loader from "../../../components/layout/Loader";

const LipidStocksListPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if (permisionsList.length > 0) checkUserPermission(['Materials Capture-Lipid Stocks']);
  }, [permisionsList]);

  const [apiRes, setApiRes] = useState(resObj);
  const [lipidStocksList, setLipidStocksList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lipidStockId, setLipidStockId] = useState('');

  const pageData = useRef({ ...paginationVal });

  const tableHeading = [
    'SI No', 'Lipid', 'Unique Lipid Stock Id', 'Solvent', 'Concentration',
    'Amount prepared', 'operator',
  ];

  const deleteModalTitle = 'Lipid Stock Delete';
  const deleteModalMessage = `Are you sure you want to delete? 
    (All related informations will be lost)`;

  useEffect(() => {
    getLipidStocks();
  }, []);

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getLipidStocks();
  };

  const navigateToCreate = () => {
    navigate('/lipid-stocks/create');
  };

  const navigateToEdit = id => {
    navigate(`/lipid-stocks/edit/${id}`);
  };

  const getLipidStocks = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get(
        '/api/materials-capture/lipid-stocks/list/get', {
        params: {
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });

      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setLipidStocksList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stock Get',
        loading: false,
      });
    }
  };

  const deleteLipidStocks = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      setDeleteModalOpen(false);
      const response = await api.delete(
        `/api/materials-capture/lipid-stocks/delete/${lipidStockId}`
      );

      if (response.status === 200) {
        getLipidStocks();
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Lipid Stock deleted successfully',
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipid Stock Delete',
        loading: false,
      });
    }
  };

  return (
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader
          title="Lipid Stocks"
          handleClick={navigateToCreate}
          search={getLipidStocks}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Materials Capture-Lipid Stocks')}
        />

        <CustomTable
          heading={tableHeading}
          rows={lipidStocksList}
          navigateToEdit={navigateToEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setLipidStockId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Role')}
          showToolTip={false}
        />

        {lipidStocksList.length === 0 && <NoData />}

        {(lipidStocksList.length !== 0 && pageData.current.pageCount !== 1) && <Pagination
          style={{ marginTop: '10px', float: 'right' }}
          count={pageData.current.pageCount}
          onChange={handlePaginationClick} />}

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

        <DeleteModal
          title={deleteModalTitle}
          message={deleteModalMessage}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={deleteLipidStocks}
        />
      </div>
    </AutoLogout>
  );
};

export default LipidStocksListPage;